import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import CircularLoading from "@saleor/autobooking/components/CircularLoading";
import {
  DateTimeUtility,
  DateTimeUtilityFilter
} from "@saleor/autobooking/utils/datetime";
import { computeBookingDateTimeDifference } from "@saleor/autobooking/utils/handlers";
import useNotifier from "@saleor/hooks/useNotifier";
import React from "react";

import GrabCancelAutobookingModal from "./GrabCancelAutobookingModal";

const useStyles = makeStyles(
  {
    buttonContainer: {
      background: "transparent",
      border: "none",
      cursor: "pointer"
    },
    cellHolder: {
      alignItems: "center",
      color: "#747C8C",
      display: "flex",
      fontWeight: 500,
      justifyContent: "space-between"
    },
    icon: {
      backgroundColor: "#EC5D77",
      borderRadius: "50%",
      color: "#FFFFFF",
      height: "18px",
      marginLeft: "5px",
      width: "18px"
    }
  },
  { name: "GrabBookingTime" }
);

export interface GrabBookingTimeProps {
  orderId: string | null;
  bookingDateTime: Date | null;
  nowDateTime: Date;
  onTimerStops: () => void;
}

const GrabBookingTime: React.FC<GrabBookingTimeProps> = props => {
  const { orderId, bookingDateTime, nowDateTime, onTimerStops } = props;
  const classes = useStyles(props);
  const notify = useNotifier();

  const SECONDS_INTERVAL = 1000;
  const [timerInterval, setTimerInterval] = React.useState<NodeJS.Timeout>(
    null
  );
  const [timerTimeout, setTimerTimeout] = React.useState<NodeJS.Timeout>(null);

  const [displayDialog, setDisplayDialog] = React.useState(false);
  const [xMins, setXMins] = React.useState(null);

  React.useEffect(() => {
    const dateTimeDifference = getBookingTimeDifference();

    if (dateTimeDifference > 0) {
      let index = 0;
      let returnValue = getUpdatedXMins(dateTimeDifference, index);
      index++;

      setTimerInterval(
        setInterval(() => {
          returnValue = getUpdatedXMins(returnValue, index);
          index++;
        }, SECONDS_INTERVAL)
      );

      setTimerTimeout(
        setTimeout(() => {
          stopInterval();
        }, dateTimeDifference)
      );
    }
  }, []);

  const getBookingTimeDisplay = (dtObj: {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    totalMinutes: number;
  }) => {
    if (dtObj.totalMinutes < 1 && dtObj.seconds > 0) {
      setXMins(
        DateTimeUtility.getDateTimeDisplay(dtObj, DateTimeUtilityFilter.ALL)
      );
    } else {
      setXMins(
        DateTimeUtility.getDateTimeDisplay(
          dtObj,
          DateTimeUtilityFilter.EXCEPT_SECONDS
        )
      );
    }
  };

  const getBookingTimeDifference = () => {
    const dateTimeDifference = computeBookingDateTimeDifference(
      bookingDateTime,
      nowDateTime
    );

    const dtObj = DateTimeUtility.getDateTimeComponents(dateTimeDifference);
    getBookingTimeDisplay(dtObj);

    return dateTimeDifference;
  };

  const getUpdatedXMins = (_timerCountdown, index) => {
    const dateTimeDifference =
      index > 0 ? _timerCountdown - 1000 : _timerCountdown;

    const dtObj = DateTimeUtility.getDateTimeComponents(dateTimeDifference);
    getBookingTimeDisplay(dtObj);

    return dateTimeDifference;
  };

  const stopInterval = () => {
    if (timerInterval) {
      clearInterval(timerInterval);
    }
    if (timerTimeout) {
      clearTimeout(timerTimeout);
    }
    onTimerStops();
  };

  const onCancelledAutobooking = () => {
    stopInterval();
    setDisplayDialog(false);
    notify({
      status: "success",
      text: `Auto-booking cancelled successfully`
    });
  };

  return (
    <>
      <GrabCancelAutobookingModal
        orderId={orderId}
        displayDialog={displayDialog}
        onClose={() => setDisplayDialog(false)}
        onSaved={onCancelledAutobooking}
        title="Cancel Auto-booking"
        content="This order has been auto-booked.
        Are you sure you want to cancel the booking?"
        isForSavingCancelAutobooking={true}
      />

      {xMins ? (
        <span className={classes.cellHolder}>
          Booking in {xMins}
          <button
            className={classes.buttonContainer}
            onClick={() => setDisplayDialog(true)}
          >
            <CloseIcon className={classes.icon} />
          </button>
        </span>
      ) : (
        <CircularLoading />
      )}
    </>
  );
};

export default GrabBookingTime;
